import React, { useState, useEffect } from 'react';
import api, { UPLOADS_URL } from '../api';

const COMMISSION_COLORS = {
  Open:     { bg: 'rgba(45,106,45,0.3)',  border: '#2d6a2d', text: '#90ee90' },
  Closed:   { bg: 'rgba(80,80,80,0.3)',   border: '#555',    text: '#aaa'    },
  Waitlist: { bg: 'rgba(139,100,0,0.3)',  border: '#8B6400', text: '#f0c060' }
};

function CommissionBadge({ status }) {
  const s = COMMISSION_COLORS[status] || COMMISSION_COLORS.Closed;
  return (
    <span className="commission-badge" style={{ background: s.bg, borderColor: s.border, color: s.text }}>
      Commissions: {status || 'Unknown'}
    </span>
  );
}

function AIBadge() {
  return (
    <span className="badge-ai" title="This artist uses AI-generated elements in some or all of their work">
      🤖 AI Elements
    </span>
  );
}

function PhotoGallery({ photos, name }) {
  const [current, setCurrent] = useState(0);
  if (!photos || photos.length === 0) return (
    <div className="carousel-empty">🎨</div>
  );
  return (
    <div className="carousel">
      <div className="carousel-img-wrapper">
        <img src={`${UPLOADS_URL}/${photos[current]}`} alt={name + ' art ' + (current + 1)}
          className="carousel-gallery-img" />
        {photos.length > 1 && (
          <>
            <button onClick={() => setCurrent(i => (i - 1 + photos.length) % photos.length)}
              className="carousel-btn carousel-btn-prev">&lsaquo;</button>
            <button onClick={() => setCurrent(i => (i + 1) % photos.length)}
              className="carousel-btn carousel-btn-next">&rsaquo;</button>
            <div className="carousel-dots">
              {photos.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)}
                  className={`carousel-dot${i === current ? ' active' : ' inactive'}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ArtistModal({ artist, onClose }) {
  const [photoIndex, setPhotoIndex] = useState(0);
  const photos = artist.photos || [];

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [onClose]);

  const linkRow = (label, url) => {
    if (!url) return null;
    const href = url.startsWith('http') ? url : 'https://' + url;
    let display = url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
    if (/instagram\.com/i.test(url)) display = '@' + url.replace(/^https?:\/\/(www\.)?instagram\.com\/@?/, '').replace(/^@/, '').replace(/\/$/, '');
    else if (/tiktok\.com/i.test(url)) display = '@' + url.replace(/^https?:\/\/(www\.)?tiktok\.com\/@?/, '').replace(/^@/, '').replace(/\/$/, '');
    else if (/facebook\.com/i.test(url)) display = url.replace(/^https?:\/\/(www\.)?facebook\.com\//, '').replace(/\/$/, '');
    else if (/youtube\.com/i.test(url)) display = url.replace(/^https?:\/\/(www\.)?youtube\.com\//, '').replace(/\/$/, '');
    return (
      <div className="link-row">
        <span className="link-label">{label}:</span>
        <a href={href} target="_blank" rel="noopener noreferrer">{display}</a>
      </div>
    );
  };

  return (
    <div className="detail-overlay" onClick={onClose}>
      <div className="detail-modal" onClick={e => e.stopPropagation()}>
        <button className="detail-modal-close" onClick={onClose}>✕</button>

        <div className="detail-modal-header">
          <h2>{artist.display_name}</h2>
          {artist.publish_full_name && artist.full_name && (
            <p className="detail-modal-location">{artist.full_name}</p>
          )}
          <p className="detail-modal-location">
            {artist.city && artist.state ? '📍 ' + artist.city + ', ' + artist.state : artist.country}
          </p>
          <div className="detail-badges">
            <CommissionBadge status={artist.commission_status} />
            {!!artist.uses_ai && <AIBadge />}
          </div>
        </div>

        {photos.length > 0 && (
          <div className="detail-photos">
            <img src={`${UPLOADS_URL}/${photos[photoIndex]}`} alt={artist.display_name + ' artwork'}
              className="detail-main-img" />
            {photos.length > 1 && (
              <div className="detail-thumbs">
                {photos.map((p, i) => (
                  <img key={i} src={`${UPLOADS_URL}/${p}`} alt={'Thumb ' + (i + 1)}
                    onClick={() => setPhotoIndex(i)}
                    className={`detail-thumb${i === photoIndex ? ' active' : ' inactive'}`} />
                ))}
              </div>
            )}
          </div>
        )}

        {artist.bio && (
          <div className="detail-section">
            <h3 className="detail-section-title">About the Artist</h3>
            <p>{artist.bio}</p>
          </div>
        )}

        {artist.products_description && (
          <div className="detail-section">
            <h3 className="detail-section-title">About Their Work</h3>
            <p>{artist.products_description}</p>
          </div>
        )}

        {(artist.art_types || artist.style_description || artist.medium || artist.price_range || artist.turnaround_time) && (
          <div className="detail-section">
            <div className="detail-info-grid">
              {artist.art_types && (
                <div>
                  <div className="info-label">Art Types</div>
                  <div className="info-value">{artist.art_types}</div>
                </div>
              )}
              {artist.medium && (
                <div>
                  <div className="info-label">Medium</div>
                  <div className="info-value">{artist.medium}</div>
                </div>
              )}
              {artist.style_description && (
                <div>
                  <div className="info-label">Style</div>
                  <div className="info-value">{artist.style_description}</div>
                </div>
              )}
              {artist.price_range && (
                <div>
                  <div className="info-label">Price Range</div>
                  <div className="info-value">💰 {artist.price_range}</div>
                </div>
              )}
              {artist.turnaround_time && (
                <div>
                  <div className="info-label">Turnaround</div>
                  <div className="info-value">⏱ {artist.turnaround_time}</div>
                </div>
              )}
              {artist.ships_physical === 'Yes' && artist.ships_to && (
                <div>
                  <div className="info-label">Ships To</div>
                  <div className="info-value">📦 {artist.ships_to}</div>
                </div>
              )}
              {artist.accepts_custom_requests === 'Yes' && (
                <div>
                  <div className="info-label">Custom Requests</div>
                  <div className="info-value">✏️ Accepted</div>
                </div>
              )}
            </div>
          </div>
        )}

        {!!artist.uses_ai && (
          <div className="detail-section">
            <div className="ai-notice">🤖 This artist's work may include AI-generated elements.</div>
          </div>
        )}

        {artist.how_to_purchase && (
          <div className="detail-section">
            <h3 className="detail-section-title">How to Purchase / Commission</h3>
            <p>{artist.how_to_purchase}</p>
          </div>
        )}

        <div className="detail-section">
          <h3 className="detail-section-title">Links</h3>
          {linkRow('Shop', artist.shop_link)}
          {linkRow('Website', artist.website)}
          {linkRow('Facebook', artist.facebook)}
          {linkRow('Instagram', artist.instagram)}
          {linkRow('TikTok', artist.tiktok)}
          {linkRow('YouTube', artist.youtube)}
          {linkRow('Other', artist.other_social)}
          {artist.publish_email && artist.email && linkRow('Email', 'mailto:' + artist.email)}
          {artist.publish_phone && artist.phone && (
            <div className="link-row">
              <span className="link-label">Phone:</span>
              <span>{artist.phone}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ArtistCard({ artist, onClick }) {
  const photos = artist.photos || [];
  return (
    <div className="dir-card" onClick={onClick}>
      <PhotoGallery photos={photos} name={artist.display_name} />
      <div className="dir-card-body">
        <h3 className="dir-card-title">{artist.display_name}</h3>
        {artist.city && artist.state && (
          <p className="dir-card-location">📍 {artist.city}, {artist.state}</p>
        )}
        <div className="dir-card-badges">
          <CommissionBadge status={artist.commission_status} />
          {!!artist.uses_ai && <AIBadge />}
        </div>
        {artist.art_types && (
          <p className="dir-card-types">
            {artist.art_types.split(',').map(t => t.trim()).filter(Boolean).join(' · ')}
          </p>
        )}
        {artist.medium && (
          <p className="dir-card-types">Medium: {artist.medium}</p>
        )}
        {artist.price_range && (
          <p className="dir-card-types">💰 {artist.price_range}</p>
        )}
        {artist.bio && (
          <p className="dir-card-bio">{artist.bio}</p>
        )}
      </div>
    </div>
  );
}

function PublicArtistsPage() {
  const [artists, setArtists] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCommission, setFilterCommission] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterArtType, setFilterArtType] = useState('');
  const [filterAI, setFilterAI] = useState('');

  useEffect(() => {
    api.get('/api/public/artists')
      .then(res => { setArtists(res.data); setFiltered(res.data); })
      .catch(() => setError('Failed to load artists.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let results = [...artists];
    if (search.trim()) {
      const q = search.toLowerCase();
      results = results.filter(a =>
        (a.display_name || '').toLowerCase().includes(q) ||
        (a.bio || '').toLowerCase().includes(q) ||
        (a.products_description || '').toLowerCase().includes(q) ||
        (a.art_types || '').toLowerCase().includes(q) ||
        (a.medium || '').toLowerCase().includes(q) ||
        (a.style_description || '').toLowerCase().includes(q) ||
        (a.city || '').toLowerCase().includes(q) ||
        (a.state || '').toLowerCase().includes(q)
      );
    }
    if (filterCommission) results = results.filter(a => a.commission_status === filterCommission);
    if (filterState) results = results.filter(a => a.state === filterState);
    if (filterArtType) results = results.filter(a => (a.art_types || '').toLowerCase().includes(filterArtType.toLowerCase()));
    if (filterAI === 'no-ai') results = results.filter(a => !a.uses_ai);
    if (filterAI === 'ai') results = results.filter(a => !!a.uses_ai);
    setFiltered(results);
  }, [search, filterCommission, filterState, filterArtType, filterAI, artists]);

  const allStates = [...new Set(artists.map(a => a.state).filter(Boolean))].sort();
  const allArtTypes = [...new Set(artists.flatMap(a => (a.art_types || '').split(',').map(t => t.trim()).filter(Boolean)))].sort();
  const clearFilters = () => { setSearch(''); setFilterCommission(''); setFilterState(''); setFilterArtType(''); setFilterAI(''); };
  const hasFilters = search || filterCommission || filterState || filterArtType || filterAI;

  if (loading) return <div className="loading">Loading artists...</div>;

  return (
    <div className="directory-page">
      <div className="directory-header">
        <h1>Krampus Artists</h1>
        <p>Discover artists creating original Krampus-themed paintings, illustrations, sculptures, digital art, and more.</p>
        {error && <p className="error-text">{error}</p>}
      </div>

      <div className="directory-filters">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search artists..." />
        <select value={filterCommission} onChange={e => setFilterCommission(e.target.value)}>
          <option value="">All Commission Status</option>
          <option value="Open">Open</option>
          <option value="Waitlist">Waitlist</option>
          <option value="Closed">Closed</option>
        </select>
        {allArtTypes.length > 0 && (
          <select value={filterArtType} onChange={e => setFilterArtType(e.target.value)}>
            <option value="">All Art Types</option>
            {allArtTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {allStates.length > 0 && (
          <select value={filterState} onChange={e => setFilterState(e.target.value)}>
            <option value="">All States / Provinces</option>
            {allStates.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <select value={filterAI} onChange={e => setFilterAI(e.target.value)}>
          <option value="">All (AI + Non-AI)</option>
          <option value="no-ai">No AI elements</option>
          <option value="ai">Includes AI elements</option>
        </select>
        {hasFilters && (
          <button onClick={clearFilters} className="btn btn-secondary btn-sm">Clear</button>
        )}
      </div>

      <p className="text-muted text-sm">
        {filtered.length} artist{filtered.length !== 1 ? 's' : ''} found
        {artists.length !== filtered.length ? ' (of ' + artists.length + ' total)' : ''}
      </p>

      {filtered.length === 0 ? (
        <div className="dir-empty">
          <p>No artists found matching your search.</p>
          <button onClick={clearFilters} className="btn btn-primary mt-2">Clear Filters</button>
        </div>
      ) : (
        <div className="directory-grid">
          {filtered.map(artist => (
            <ArtistCard key={artist.id} artist={artist} onClick={() => setSelected(artist)} />
          ))}
        </div>
      )}

      {selected && <ArtistModal artist={selected} onClose={() => setSelected(null)} />}

      <div className="directory-cta">
        <h3>Are you a Krampus artist?</h3>
        <p className="text-muted">Get listed in the directory and connect with collectors and fans.</p>
        <a href="/register-artist" className="btn btn-primary">Register as an Artist</a>
      </div>
    </div>
  );
}

export default PublicArtistsPage;
